/**
 * Buy Now Button Component
 * Handles adding products to cart with dispatch calculation and redirects to checkout
 */
class BuyNowButton {
  constructor() {
    this.debug = window.themeSettings?.dispatch_debug_mode || false;
    this.init();
  }

  init() {
    if (this.debug) console.log('BuyNowButton initialized');
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Listen for cart changes to show/hide buy now buttons
    document.addEventListener('cart:updated', () => {
      this.updateButtonVisibility();
    });

    document.addEventListener('cart:item-added', () => {
      this.updateButtonVisibility();
    });

    document.addEventListener('cart:emptied', () => {
      this.updateButtonVisibility();
    });

    // Listen for Shopify section reloads
    document.addEventListener('shopify:section:load', () => {
      this.updateButtonVisibility();
    });

    // Initial visibility check on page load
    document.addEventListener('DOMContentLoaded', () => {
      this.updateButtonVisibility();
    });

    // Handle browser navigation to reset button states and visibility
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.resetButtonStates();
        this.updateButtonVisibility();
      }
    });

    window.addEventListener('popstate', () => {
      this.resetButtonStates();
      this.updateButtonVisibility();
    });

    // Handle page transition events if they exist
    document.addEventListener('pageTransitionComplete', () => {
      this.resetButtonStates();
      this.updateButtonVisibility();
    });

    if (this.debug) {
      console.log('Buy now button event listeners setup complete');
    }
  }


  /**
   * Update button visibility based on cart state
   */
  async updateButtonVisibility() {
    try {
      // Fetch current cart state
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const isEmpty = cart.item_count === 0;
      const buyNowButtons = document.querySelectorAll('[data-cart-empty-only="true"]');
      
      buyNowButtons.forEach(button => {
        if (isEmpty) {
          this.showButton(button);
          if (this.debug) {
            console.log('Buy now button shown - cart is empty');
          }
        } else {
          this.hideButton(button);
          if (this.debug) {
            console.log('Buy now button hidden - cart has items:', cart.item_count);
          }
        }
      });
      
    } catch (error) {
      console.error('Error checking cart state for buy now button:', error);
    }
  }

  /**
   * Show button with smooth animation
   * @param {HTMLElement} button - Button container element
   */
  showButton(button) {
    if (button.style.display === 'block') return; // Already visible
    
    button.style.display = 'block';
    button.classList.remove('hiding');
    button.classList.add('showing');
    
    // Force reflow to ensure display change takes effect
    button.offsetHeight;
    
    setTimeout(() => {
      button.classList.remove('showing');
    }, 300);
  }

  /**
   * Hide button with smooth animation
   * @param {HTMLElement} button - Button container element
   */
  hideButton(button) {
    if (button.style.display === 'none') return; // Already hidden
    
    button.classList.remove('showing');
    button.classList.add('hiding');
    
    setTimeout(() => {
      button.style.display = 'none';
      button.classList.remove('hiding');
    }, 300);
  }

  /**
   * Reset all buy now button states to their default state
   */
  resetButtonStates() {
    const buyNowButtons = document.querySelectorAll('.buy-now-btn');
    buyNowButtons.forEach(button => {
      button.disabled = false;
      button.classList.remove('loading');
      
      const label = button.querySelector('.button-label');
      if (label) {
        // Reset to original text from data attribute
        const buttonContainer = button.closest('.buy-now-button');
        const originalText = buttonContainer?.dataset?.buttonText || 'Buy Now';
        label.textContent = originalText;
      }
    });

    if (this.debug) {
      console.log('Buy now button states reset');
    }
  }
}

// Initialize the buy now button component
window.buyNowButton = new BuyNowButton();
